import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loadingLabel?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  fullWidth?: boolean;
  disabledReason?: string;
}

const variantStyles: Record<string, string> = {
  primary: 'btn btn--primary',
  secondary: 'btn btn--secondary',
  ghost: 'btn btn--ghost',
  outline: 'btn btn--outline',
  danger: 'btn btn--danger',
};

const sizeStyles: Record<string, string> = {
  sm: 'min-h-[2.25rem] px-3 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'min-h-12 px-5 py-2.5 text-base gap-2.5',
};

const Spinner = () => (
  <svg className="btn__spinner h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  loadingLabel,
  icon,
  iconPosition = 'start',
  fullWidth = false,
  disabledReason,
  className = '',
  disabled,
  title,
  ...props
}) => {
  const isDisabled = disabled || isLoading;
  const resolvedTitle = isDisabled && disabledReason ? disabledReason : title;
  const content = isLoading && loadingLabel ? loadingLabel : children;

  return (
    <button
      className={`${variantStyles[variant]} ${sizeStyles[size]} ${fullWidth ? 'w-full' : ''} ${isLoading ? 'btn--loading' : ''} ${className}`}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      data-state={isLoading ? 'loading' : isDisabled ? 'disabled' : 'idle'}
      data-disabled-reason={disabledReason ?? undefined}
      title={resolvedTitle}
      {...props}
    >
      {!isLoading && icon && iconPosition === 'start' ? <span className="flex-shrink-0">{icon}</span> : null}
      {isLoading ? <Spinner /> : null}
      <span className="truncate">{content}</span>
      {!isLoading && icon && iconPosition === 'end' ? <span className="flex-shrink-0">{icon}</span> : null}
    </button>
  );
};

export default Button;
